import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import type { SupportedLanguage } from '@/i18n'

export function NewsMetadata({ language, title, description, canonicalPath, alternatePaths, image, publishedTime, notFound = false }: {
  language: SupportedLanguage
  title: string
  description: string
  canonicalPath: string
  alternatePaths: Record<SupportedLanguage, string>
  image?: string | null
  publishedTime?: string | null
  notFound?: boolean
}) {
  const { t } = useTranslation('common')
  useEffect(() => {
    const previousTitle = document.title
    const pageTitle = t('news.metaTitle', { title })
    document.title = pageTitle
    const restore: (() => void)[] = []
    function set(tag: 'meta' | 'link', selector: string, attributes: Record<string, string>) {
      const existing = document.head.querySelector(selector)
      const node = existing ?? document.createElement(tag)
      const old = node.getAttributeNames().map((name) => [name, node.getAttribute(name)!] as const)
      Object.entries(attributes).forEach(([key, value]) => node.setAttribute(key, value))
      if (!existing) document.head.append(node)
      restore.push(() => {
        if (!existing) node.remove()
        else {
          node.getAttributeNames().forEach((name) => node.removeAttribute(name))
          old.forEach(([name, value]) => node.setAttribute(name, value))
        }
      })
    }
    const origin = 'https://website2026-esamed-it.pages.dev'
    const canonical = `${origin}${canonicalPath}`
    set('meta', 'meta[name="description"]', { name: 'description', content: description })
    set('meta', 'meta[name="robots"]', { name: 'robots', content: notFound ? 'noindex, follow' : 'index, follow' })
    set('link', 'link[rel="canonical"]', { rel: 'canonical', href: canonical })
    for (const locale of ['it', 'en'] as const) {
      set('link', `link[rel="alternate"][hreflang="${locale}"]`, { rel: 'alternate', hreflang: locale, href: `${origin}${alternatePaths[locale]}` })
    }
    set('link', 'link[rel="alternate"][hreflang="x-default"]', { rel: 'alternate', hreflang: 'x-default', href: `${origin}${alternatePaths.it}` })
    const socialImage = image ? `${origin}${image}` : `${origin}/og-image.png`
    const social: Record<string, string> = {
      'og:title': pageTitle,
      'og:description': description,
      'og:url': canonical,
      'og:type': canonicalPath.includes('?article=') ? 'article' : 'website',
      'og:locale': language === 'it' ? 'it_IT' : 'en_GB',
      'og:site_name': t('brand.name'),
      'og:image': socialImage,
      'og:image:alt': image ? title : t('brand.socialImageAlt'),
    }
    for (const [property, content] of Object.entries(social)) set('meta', `meta[property="${property}"]`, { property, content })
    for (const [name, content] of Object.entries({ 'twitter:card': 'summary_large_image', 'twitter:title': pageTitle, 'twitter:description': description, 'twitter:image': socialImage, 'twitter:image:alt': image ? title : t('brand.socialImageAlt') })) set('meta', `meta[name="${name}"]`, { name, content })
    if (publishedTime) set('meta', 'meta[property="article:published_time"]', { property: 'article:published_time', content: publishedTime })
    if (canonicalPath.includes('?article=')) {
      const existing = document.head.querySelector<HTMLScriptElement>('script[data-news-structured-data]')
      const script = existing ?? document.createElement('script')
      const oldText = script.textContent
      script.type = 'application/ld+json'
      script.dataset.newsStructuredData = ''
      script.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'NewsArticle',
        headline: title,
        description,
        mainEntityOfPage: canonical,
        inLanguage: language,
        ...(publishedTime ? { datePublished: publishedTime } : {}),
        ...(image ? { image: `${origin}${image}` } : {}),
        publisher: { '@type': 'Organization', name: 'Esamed S.r.l.' },
      })
      if (!existing) document.head.append(script)
      restore.push(() => {
        if (!existing) script.remove()
        else script.textContent = oldText
      })
    }
    return () => {
      document.title = previousTitle
      restore.forEach((action) => action())
    }
  }, [alternatePaths, canonicalPath, description, image, language, notFound, publishedTime, t, title])
  return null
}
